/**
 * mobileTrainingLaunchState — short-lived, client-only record of an UNVERIFIED
 * mobile training launch, used to power the return + self-attestation prompt.
 *
 * Stored in localStorage (NOT sessionStorage) so it survives iOS evicting Safari
 * from memory while the YouTube app is foregrounded. The record is non-sensitive:
 * artifact/ticket ids, a provider URL, a launch timestamp, and (if known) the
 * artifact duration in minutes. It NEVER holds a token, session id, or secret.
 *
 * This is NOT evidence. Nothing here is persisted to the backend until the user
 * explicitly self-attests ("I finished watching"). The record is a client-side
 * timer + context only.
 */

export const MOBILE_TRAINING_LAUNCH_KEY = 'optibay_mobile_training_launch';
export const MOBILE_TRAINING_LAUNCH_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

export interface MobileTrainingLaunchRecord {
  artifactId: string;
  ticketId: string | null;
  technicianProfileId: string | null;
  providerUrl: string;
  launchedAtMs: number;
  durationMinutes: number | null;
  source: 'mobile_unverified';
  /**
   * When resuming an existing mobile partial from Passport History, the id of the
   * session row to UPDATE (so a resume never creates a duplicate history row).
   * Absent for a fresh launch from the ticket training tab.
   */
  existingSessionId: string | null;
  expiresAtMs: number;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Write the launch record. The timer STARTS here — callers pass `launchedAtMs`
 * captured at the exact moment the user tapped "Open training".
 */
export function writeMobileTrainingLaunch(args: {
  artifactId: string;
  ticketId: string | null;
  technicianProfileId: string | null;
  providerUrl: string;
  durationMinutes: number | null;
  launchedAtMs: number;
  existingSessionId?: string | null;
}): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  const record: MobileTrainingLaunchRecord = {
    artifactId: args.artifactId,
    ticketId: args.ticketId,
    technicianProfileId: args.technicianProfileId,
    providerUrl: args.providerUrl,
    launchedAtMs: args.launchedAtMs,
    durationMinutes:
      typeof args.durationMinutes === 'number' && args.durationMinutes > 0
        ? args.durationMinutes
        : null,
    source: 'mobile_unverified',
    existingSessionId: args.existingSessionId ?? null,
    expiresAtMs: args.launchedAtMs + MOBILE_TRAINING_LAUNCH_TTL_MS,
  };
  try {
    ls.setItem(MOBILE_TRAINING_LAUNCH_KEY, JSON.stringify(record));
  } catch {
    /* best-effort — quota/private-mode failures are non-fatal */
  }
}

export function clearMobileTrainingLaunch(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.removeItem(MOBILE_TRAINING_LAUNCH_KEY);
  } catch {
    /* best-effort */
  }
}

function isValidShape(v: unknown): v is MobileTrainingLaunchRecord {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.artifactId === 'string' &&
    typeof r.ticketId === 'string' &&
    typeof r.providerUrl === 'string' &&
    typeof r.launchedAtMs === 'number' &&
    typeof r.expiresAtMs === 'number' &&
    (r.durationMinutes === null || typeof r.durationMinutes === 'number') &&
    (r.technicianProfileId === null ||
      typeof r.technicianProfileId === 'string')
  );
}

/**
 * Return the current launch record if present, well-formed, and NOT expired.
 * Expired or malformed records are cleared and `null` is returned. Optionally
 * scope to a specific ticket/artifact — a mismatch clears and returns null.
 */
export function readValidMobileTrainingLaunch(
  nowMs: number,
  match?: { ticketId?: string; artifactId?: string },
): MobileTrainingLaunchRecord | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  let raw: string | null;
  try {
    raw = ls.getItem(MOBILE_TRAINING_LAUNCH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearMobileTrainingLaunch();
    return null;
  }
  if (!isValidShape(parsed)) {
    clearMobileTrainingLaunch();
    return null;
  }
  if (nowMs >= parsed.expiresAtMs) {
    clearMobileTrainingLaunch();
    return null;
  }
  if (match?.ticketId && match.ticketId !== parsed.ticketId) {
    clearMobileTrainingLaunch();
    return null;
  }
  if (match?.artifactId && match.artifactId !== parsed.artifactId) {
    clearMobileTrainingLaunch();
    return null;
  }
  return parsed;
}
